import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useRedObjects } from '../hooks'
import { formatBytes } from '../utils/formatSize'
import { AnalyticsStatusIndicator } from '@/lib/utils/analytics-status'
import { formatDateTime } from '@/lib/dateUtils'

interface RedObjectsListDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    objectID: string | null
}

export function RedObjectsListDialog({
    open,
    onOpenChange,
    objectID,
}: RedObjectsListDialogProps) {
    const { t, i18n } = useTranslation()
    const { data: redObjects = [], isLoading } = useRedObjects(objectID)

    const getStatusCircle = (analytics: number | null) => (
        <AnalyticsStatusIndicator status={analytics} size="h-4 w-4" />
    )

    const formatDate = (datetime: string | Date | null | undefined) => {
        if (!datetime) return '-'
        // Skip if it's an empty object
        if (typeof datetime === 'object' && !(datetime instanceof Date) && Object.keys(datetime).length === 0) {
            return '-'
        }
        // Handle string dates from MySQL (format: 'YYYY-MM-DD HH:mm:ss') or ISO strings
        let date: Date
        if (datetime instanceof Date) {
            date = datetime
        } else if (typeof datetime === 'string') {
            // Handle ISO format or MySQL datetime format
            const dateStr = datetime.includes('T') ? datetime : datetime.replace(' ', 'T')
            date = new Date(dateStr)
        } else {
            return '-'
        }
        if (isNaN(date.getTime())) {
            return '-'
        }
        return (
            <span className="whitespace-nowrap">
                {formatDateTime(date, i18n.language)}
            </span>
        )
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[1400px] max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{t('objects.redObjectsList')}</DialogTitle>
                    <DialogDescription>
                        {t('objects.redObjectsListDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="py-4">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="text-muted-foreground">{t('common.loading')}</div>
                        </div>
                    ) : redObjects.length === 0 ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="text-muted-foreground">{t('objects.noRedObjects')}</div>
                        </div>
                    ) : (
                        <div className="border rounded-md overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="min-w-[100px]">{t('objects.redAnalytics')}</TableHead>
                                        <TableHead className="min-w-[160px]">{t('objects.redDateTime')}</TableHead>
                                        <TableHead className="min-w-[100px]">{t('objects.redSize')}</TableHead>
                                        <TableHead className="min-w-[100px]">{t('objects.greenAnalytics')}</TableHead>
                                        <TableHead className="min-w-[160px]">{t('objects.greenDateTime')}</TableHead>
                                        <TableHead className="min-w-[100px]">{t('objects.greenSize')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {redObjects.map((obj, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    {getStatusCircle(obj.redAnalytics)}
                                                    <span className="text-sm">{obj.redAnalytics !== null && obj.redAnalytics !== undefined ? obj.redAnalytics : '-'}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {formatDate(obj.redDateTime)}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {obj.redSize !== null && obj.redSize !== undefined
                                                    ? formatBytes(obj.redSize)
                                                    : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    {getStatusCircle(obj.greenAnalytics)}
                                                    <span className="text-sm">{obj.greenAnalytics !== null && obj.greenAnalytics !== undefined ? obj.greenAnalytics : '-'}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {formatDate(obj.greenDateTime)}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {obj.greenSize !== null && obj.greenSize !== undefined
                                                    ? formatBytes(obj.greenSize)
                                                    : '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button onClick={() => onOpenChange(false)}>
                        {t('common.close')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
